import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { 
  Magnet, 
  ArrowLeft,
  Copy,
  Eye,
  Briefcase,
  Heart,
  GraduationCap,
  Loader2,
  Target,
  Users,
  BookOpen,
  Lightbulb,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

type FieldType = "email" | "phone" | "full_name" | "first_name" | "job_title" | "company" | "custom_text" | "custom_select";

interface TemplateField {
  field_type: FieldType;
  label: string;
  placeholder?: string;
  options?: string[];
  is_required: boolean;
  sort_order: number;
}

interface Template {
  id: string;
  title: string;
  description: string;
  icon_name: string;
  color_primary: string;
  color_secondary: string;
  color_accent: string;
  color_text_light: string;
  color_text_dark: string;
  ai_question_count: number;
  ai_example_questions: string[];
  ai_final_prompt: string;
  thank_you_message: string;
  fields: TemplateField[];
}

const iconMap: Record<string, React.ReactNode> = {
  Briefcase: <Briefcase className="h-8 w-8" />,
  Heart: <Heart className="h-8 w-8" />,
  GraduationCap: <GraduationCap className="h-8 w-8" />,
  Magnet: <Magnet className="h-8 w-8" />,
  Target: <Target className="h-8 w-8" />,
  Users: <Users className="h-8 w-8" />,
  BookOpen: <BookOpen className="h-8 w-8" />,
  Lightbulb: <Lightbulb className="h-8 w-8" />,
};

export default function Templates() {
  const { user, loading: authLoading, isAuthenticated } = useAuth();
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [duplicatingId, setDuplicatingId] = useState<string | null>(null);
  const [previewTemplate, setPreviewTemplate] = useState<Template | null>(null);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const { data, error } = await supabase
        .from("templates")
        .select("*")
        .eq("is_active", true)
        .order("sort_order");

      if (error) throw error;

      const parsed = (data || []).map((t) => ({
        ...t,
        fields: (t.fields as unknown as TemplateField[]) || [],
      }));

      setTemplates(parsed);
    } catch (error) {
      console.error("Error fetching templates:", error);
      toast({ title: "Erro ao carregar templates", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const getIcon = (iconName: string) => {
    return iconMap[iconName] || <Briefcase className="h-8 w-8" />;
  };

  const handleDuplicate = async (template: Template) => {
    if (!isAuthenticated) {
      toast({
        title: "Login necessário",
        description: "Faça login para duplicar este template.",
        variant: "destructive",
      });
      navigate("/auth");
      return;
    }

    setDuplicatingId(template.id);

    try {
      const newSlug = `${template.title.toLowerCase().replace(/\s+/g, "-")}-${Date.now().toString(36)}`;

      // Create form from template
      const { data: newForm, error: createError } = await supabase
        .from("forms")
        .insert({
          title: template.title,
          slug: newSlug,
          description: template.description,
          is_active: false,
          logo_url: null,
          color_primary: template.color_primary,
          color_secondary: template.color_secondary,
          color_accent: template.color_accent,
          color_text_light: template.color_text_light,
          color_text_dark: template.color_text_dark,
          ai_question_count: template.ai_question_count,
          ai_example_questions: template.ai_example_questions,
          ai_final_prompt: template.ai_final_prompt,
          thank_you_message: template.thank_you_message,
          use_lead_history: false,
          user_id: user?.id,
        })
        .select()
        .single();

      if (createError) throw createError;

      // Create form fields
      if (template.fields && template.fields.length > 0) {
        const newFields = template.fields.map((field, index) => ({
          form_id: newForm.id,
          field_type: field.field_type,
          label: field.label,
          placeholder: field.placeholder || null,
          options: field.options || null,
          is_required: field.is_required,
          sort_order: typeof field.sort_order === "number" ? field.sort_order : index,
        }));

        const { error: insertFieldsError } = await supabase
          .from("form_fields")
          .insert(newFields);

        if (insertFieldsError) throw insertFieldsError;
      }

      toast({
        title: "Template duplicado!",
        description: "O template foi adicionado aos seus formulários.",
      });

      navigate(`/form-builder/${newForm.id}`);
    } catch (error) {
      console.error("Error duplicating template:", error);
      toast({
        title: "Erro ao duplicar",
        description: "Não foi possível duplicar o template.",
        variant: "destructive",
      });
    } finally {
      setDuplicatingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-hero">
      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-9 h-9 bg-gradient-primary rounded-xl flex items-center justify-center shadow-glow">
              <Magnet className="h-4 w-4 text-primary-foreground" />
            </div>
            <span className="text-xl font-display font-bold">Magnet Link</span>
          </Link>
          <Button variant="ghost" asChild>
            <Link to="/dashboard">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Voltar ao Dashboard
            </Link>
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8 pb-[100px]">
        <div className="text-center mb-12">
          <h1 className="text-3xl md:text-4xl font-display font-bold mb-4">
            Templates de Formulários
          </h1>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Escolha um template pronto para começar rapidamente. Cada um já vem configurado com 
            campos, perguntas de IA e prompt para gerar o PDF final personalizado.
          </p>
        </div>

        {loading ? (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <Card key={i}>
                <CardHeader>
                  <Skeleton className="h-12 w-12 rounded-xl" />
                  <Skeleton className="h-6 w-3/4 mt-4" />
                </CardHeader>
                <CardContent>
                  <Skeleton className="h-20 w-full" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : templates.length === 0 ? (
          <Card className="text-center py-16">
            <CardContent>
              <Briefcase className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-xl font-semibold mb-2">Nenhum template disponível</h3>
              <p className="text-muted-foreground">Os templates estão sendo preparados. Volte em breve!</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {templates.map((template) => (
              <Card 
                key={template.id} 
                className="group hover:shadow-xl transition-all duration-300 overflow-hidden"
              >
                <div 
                  className="h-3"
                  style={{ background: `linear-gradient(to right, ${template.color_primary}, ${template.color_accent})` }}
                />
                <CardHeader className="pb-3">
                  <div className="flex items-start gap-4">
                    <div 
                      className="p-3 rounded-xl"
                      style={{ backgroundColor: template.color_secondary, color: template.color_primary }}
                    >
                      {getIcon(template.icon_name)}
                    </div>
                    <div className="flex-1">
                      <CardTitle className="text-lg font-display">
                        {template.title}
                      </CardTitle>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <CardDescription className="mb-4 min-h-[60px]">
                    {template.description}
                  </CardDescription>
                  
                  <div className="space-y-2 text-sm text-muted-foreground mb-6">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{template.fields?.length || 0}</span> campos pré-configurados
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{template.ai_question_count}</span> perguntas de IA
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{template.ai_example_questions?.length || 0}</span> exemplos de perguntas
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-2">
                    <Button 
                      variant="outline" 
                      className="flex-1 w-full"
                      onClick={() => setPreviewTemplate(template)}
                    >
                      <Eye className="mr-2 h-4 w-4" />
                      Ver detalhes
                    </Button>
                    <Button 
                      className="flex-1 w-full"
                      style={{ 
                        background: `linear-gradient(to right, ${template.color_primary}, ${template.color_accent})`,
                      }}
                      onClick={() => handleDuplicate(template)}
                      disabled={duplicatingId === template.id}
                    >
                      {duplicatingId === template.id ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <Copy className="mr-2 h-4 w-4" />
                      )}
                      Usar template
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Preview Dialog */}
      <Dialog open={!!previewTemplate} onOpenChange={() => setPreviewTemplate(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto pb-[100px]">
          {previewTemplate && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-3">
                  <div 
                    className="p-3 rounded-xl"
                    style={{ backgroundColor: previewTemplate.color_secondary, color: previewTemplate.color_primary }}
                  >
                    {getIcon(previewTemplate.icon_name)}
                  </div>
                  <div>
                    <DialogTitle className="font-display">{previewTemplate.title}</DialogTitle>
                    <DialogDescription>{previewTemplate.description}</DialogDescription>
                  </div>
                </div>
              </DialogHeader>

              <div className="space-y-6 mt-4">
                {/* Fields */}
                <div>
                  <h4 className="font-semibold mb-3">Campos do formulário:</h4>
                  <div className="space-y-2">
                    {previewTemplate.fields?.map((field, index) => (
                      <div key={index} className="flex items-center gap-2 text-sm p-2 bg-muted rounded-lg">
                        <span className="font-medium">{field.label}</span>
                        {field.is_required && <span className="text-destructive text-xs">*</span>}
                        <span className="text-muted-foreground text-xs ml-auto">
                          {field.field_type.replace("_", " ")}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* AI Questions */}
                <div>
                  <h4 className="font-semibold mb-3">Exemplos de perguntas da IA:</h4>
                  <ul className="space-y-2">
                    {previewTemplate.ai_example_questions?.map((question, index) => (
                      <li key={index} className="text-sm text-muted-foreground flex gap-2">
                        <span className="text-primary font-medium">{index + 1}.</span>
                        {question}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* PDF Prompt */}
                <div>
                  <h4 className="font-semibold mb-3">Prompt para gerar o PDF:</h4>
                  <p className="text-sm text-muted-foreground bg-muted p-3 rounded-lg">
                    {previewTemplate.ai_final_prompt}
                  </p>
                </div>

                {/* Thank you message */}
                <div>
                  <h4 className="font-semibold mb-3">Mensagem de agradecimento:</h4>
                  <p className="text-sm text-muted-foreground italic">
                    "{previewTemplate.thank_you_message}"
                  </p>
                </div>

                <Button 
                  className="w-full"
                  style={{ 
                    background: `linear-gradient(to right, ${previewTemplate.color_primary}, ${previewTemplate.color_accent})`,
                  }}
                  onClick={() => {
                    setPreviewTemplate(null);
                    handleDuplicate(previewTemplate);
                  }}
                  disabled={duplicatingId === previewTemplate.id}
                >
                  {duplicatingId === previewTemplate.id ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Copy className="mr-2 h-4 w-4" />
                  )}
                  Usar este template
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
